import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import Image from 'components/Common/Image'
import { rem } from 'polished'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { tierGradientBackgroundStyle, tierGradientSecondaryBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'

const HeroSection = styled.div`
  background-color: ${props => props.theme.palette.neutral.control.black100};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
`

const HeroGrid = styled.div`
  display: grid;
  gap: ${rem(24)};
  padding: ${rem(24)} ${rem(8)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 2fr 3fr;
    gap: ${rem(40)};
    align-items: center;
    padding: ${rem(40)} 0;
  }
`

const BackLinkBar = styled.div`
  padding: ${rem(12)} 0;
`

const BackLink = styled(ClickableLink)`
  &:hover {
    text-decoration: underline;
  }
`

const MobileImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(150)};
  overflow: hidden;
`

const DesktopImageContainer = styled.div`
  position: relative;
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;
  height: ${rem(290)};
`

const BrowseLoungesButton = styled(TextButton)`
  align-self: flex-start;
  border-width: 1px;
  border-color: ${props => props.theme.palette.neutral.surface.normalForeground};
`

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(40)};
  }
`

const LoungePassBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(8)} ${rem(16)} ${rem(8)} ${rem(8)};
  border-radius: ${rem(99)};

  ${tierGradientSecondaryBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

const BadgeCoin = styled.div`
  width: ${rem(24)};
  height: ${rem(24)};
  border-radius: 50%;
  flex-shrink: 0;
  display: grid;
  place-items: center;

  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

const BadgeContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

const BadgeStats = styled.div``

const BadgeLogo = styled(LuxuryEscapesSmallLogo)`
  path {
    fill: currentColor;
  }
`

const LOUNGE_DIRECTORY_ID = 'lounges-directory'

function handleBrowseLoungesClick() {
  document.getElementById(LOUNGE_DIRECTORY_ID)?.scrollIntoView({ behavior: 'smooth' })
}

function LoungePassesHeroSection() {
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const loungePassBenefit = useLoyaltyUserBenefitView('lounge_pass')
  const statusReviewDate = useAppSelector(state => state.loyalty.account.data?.statusReviewDate)

  const isUnlimited = loungePassBenefit?.benefit.unlimited ?? false
  const remaining = isUnlimited ? undefined : (loungePassBenefit?.benefit.remaining ?? 0)
  const redeemed = isUnlimited ? undefined : ((loungePassBenefit?.benefit.limit ?? 0) - (loungePassBenefit?.benefit.remaining ?? 0))

  const passCountStats = isUnlimited
    ? <BodyText variant="medium" weight="bold">
        <FormattedMessage
          id="URGSGDDNclkTvOOsZvW2"
          defaultMessage="Unlimited"
          description="Unlimited lounge passes label in the hero benefit badge"
        />
    </BodyText>
    : <Group direction="horizontal" gap={4} verticalAlign="center">
        <BodyText variant="medium" weight="bold">{remaining}</BodyText>
        <BodyText variant="medium">
          <FormattedMessage
            id="uOXjPnORDDEbvO1Ofj9A"
            defaultMessage="Remaining"
            description="Remaining lounge passes count label in the hero benefit badge"
          />
        </BodyText>
        <BodyText variant="medium">
          <FormattedMessage
            id="_pdyOlB8F2otequIwl0m"
            defaultMessage="·"
            description="Separator dot between remaining and redeemed lounge pass counts"
          />
        </BodyText>
        <BodyText variant="medium" weight="bold">{redeemed}</BodyText>
        <BodyText variant="medium">
          <FormattedMessage
            id="iGIsNYd879VS2CUpjWOK"
            defaultMessage="Redeemed"
            description="Redeemed lounge passes count label in the hero benefit badge"
          />
        </BodyText>
    </Group>

  const loungePassStats = isLoggedIn && loungePassBenefit
    ? <LoungePassBadge className={`tier-${tier}`}>
        <BadgeCoin className={`tier-${tier}`}>
          <BadgeLogo height={rem(12)} />
        </BadgeCoin>
        <BadgeContent>
          <BadgeStats>
            {passCountStats}
          </BadgeStats>
          {statusReviewDate && <Group direction="horizontal" gap={4} verticalAlign="center">
            <BodyText variant="medium">
              <FormattedMessage
                id="JtG98h1nXSGpuyfoZnYN"
                defaultMessage="Expires"
                description="Expiry label for lounge passes in the hero benefit badge"
              />
            </BodyText>
            <BodyText variant="medium" weight="bold">
              <FormatDateTime value={statusReviewDate} format="long-month-long-year" />
            </BodyText>
          </Group>}
        </BadgeContent>
    </LoungePassBadge>
    : <Group direction="horizontal" gap={8} verticalAlign="center">
        <Caption variant="medium" weight="bold" colour="dark-contrast">
          <FormattedMessage
            id="imJuK8VxhiWojpV0mQTO"
            defaultMessage="AVAILABLE FOR:"
            description="Label preceding the tier eligibility badges on the lounge passes page"
          />
        </Caption>
        <Group direction="horizontal" gap={4}>
          <LoyaltyTierLabel tier="silver" kind="primary">
            <FormattedMessage
              id="-ZZE0RjWilYOgIeeYVYr"
              defaultMessage="SILVER"
              description="Silver tier eligibility badge label on the lounge passes page"
            />
          </LoyaltyTierLabel>
          <LoyaltyTierLabel tier="gold" kind="primary">
            <FormattedMessage
              id="_CwGnFOSZSLfRpmBV8Mn"
              defaultMessage="GOLD"
              description="Gold tier eligibility badge label on the lounge passes page"
            />
          </LoyaltyTierLabel>
          <LoyaltyTierLabel tier="platinum" kind="primary">
            <FormattedMessage
              id="6hddp1qA89ZY7L8XhMDO"
              defaultMessage="PLATINUM"
              description="Platinum tier eligibility badge label on the lounge passes page"
            />
          </LoyaltyTierLabel>
        </Group>
    </Group>

  return <HeroSection>
    <CSSBreakpoint max="mobile">
      <LayoutContainer variant="large">
        <BackLinkBar>
          <BackLink to={`/${LOYALTY_LANDING_PAGE}#benefits`}>
            <BodyTextBlock variant="medium" startIcon={<LineAngleLeftIcon />} colour="dark-contrast">
              <FormattedMessage
                id="uyIL-7CbftRGyVM3tuGP"
                defaultMessage="All benefits"
                description="Back link to all Société benefits from lounge passes page"
              />
            </BodyTextBlock>
          </BackLink>
        </BackLinkBar>
      </LayoutContainer>
      <MobileImageContainer>
        <Image
          id="rq13k5lzgr8oxoz3a2"
          alt=""
          fit="center"
          loading="eager"
          dpr={2}
        />
      </MobileImageContainer>
    </CSSBreakpoint>
    <LayoutContainer variant="large">
      <HeroGrid>
        <HeroContent>
          <CSSBreakpoint min="tablet">
            <BackLink to={`/${LOYALTY_LANDING_PAGE}#benefits`}>
              <BodyTextBlock variant="medium" startIcon={<LineAngleLeftIcon />} colour="dark-contrast">
                <FormattedMessage
                  id="uyIL-7CbftRGyVM3tuGP"
                  defaultMessage="All benefits"
                  description="Back link to all Société benefits from lounge passes page"
                />
              </BodyTextBlock>
            </BackLink>
          </CSSBreakpoint>
          <VerticalSpacer gap={20}>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <LoyaltyProgramNameLogo width={104} height={21} fill="currentColor" />
              {isLoggedIn && <LoyaltyTierLabel tier={tier} kind="primary">
                {getLoyaltyTierDisplayName(tier)}
              </LoyaltyTierLabel>}
            </Group>
            <VerticalSpacer gap={8}>
              <Heading variant="heading2">
                <FormattedMessage
                  id="vI1lgQfuV9mjbFDizTjf"
                  defaultMessage="Free airport lounge passes"
                  description="Main hero heading displayed at the top of the lounge passes benefit page"
                />
              </Heading>
              <BodyText variant="large">
                <FormattedMessage
                  id="8lK_eCkQA4LER1z98p2c"
                  defaultMessage="Unwind with a drink, refreshments and plenty of space to relax before your international flight."
                  description="Hero description on the lounge passes benefit page"
                />
              </BodyText>
              {loungePassStats}
            </VerticalSpacer>
          </VerticalSpacer>
          <BrowseLoungesButton
            kind="secondary"
            variant="ghost"
            fit="mobile-full-width"
            onClick={handleBrowseLoungesClick}
          >
            <FormattedMessage
              id="76Im4lbrTifopOT7aHXR"
              defaultMessage="Browse lounges"
              description="CTA button on the lounge passes hero that scrolls to the directory section"
            />
          </BrowseLoungesButton>
        </HeroContent>
        <CSSBreakpoint min="tablet">
          <DesktopImageContainer>
            <Image
              id="rq13k5lzgr8oxoz3a2"
              alt=""
              fit="center"
              loading="eager"
              dpr={2}
            />
          </DesktopImageContainer>
        </CSSBreakpoint>
      </HeroGrid>
    </LayoutContainer>
  </HeroSection>
}

export { LOUNGE_DIRECTORY_ID }
export default LoungePassesHeroSection
